/**
 * GeoModelTransform — converts between WGS84 lat/lng and terrain-model coordinates
 * using the georef calibration delivered by the host in the bridge `init` message
 * (Bridge Contract v0.2 §6).
 *
 * Model-space convention (before rotation): +X = east, -Z = north, Y = up.
 * `rotationDeg` rotates the geo frame around +Y to match the GLB's actual orientation;
 * `metersPerUnit` scales meters → model units; `originModel` anchors the geo origin
 * in model space. Heights come from terrain raycasts, not from the geodata.
 *
 * The local tangent-plane (equirectangular) approximation is used — for a valley-sized
 * area (< a few km) the error is far below pin-placement tolerance.
 */
import { BridgeGeoref } from './MapBridge';

const METERS_PER_DEG_LAT = 111320;

export class GeoModelTransform {
    private georef: BridgeGeoref;
    private mPerDegLng: number;
    private cosR: number;
    private sinR: number;

    constructor(georef: BridgeGeoref) {
        this.georef = georef;
        this.mPerDegLng = METERS_PER_DEG_LAT * Math.cos((georef.originLat * Math.PI) / 180);
        const r = (georef.rotationDeg * Math.PI) / 180;
        this.cosR = Math.cos(r);
        this.sinR = Math.sin(r);
    }

    /** lat/lng → model-space x/z (y must come from a terrain raycast). */
    public geoToModel(lat: number, lng: number): { x: number; z: number } {
        const eastM = (lng - this.georef.originLng) * this.mPerDegLng;
        const northM = (lat - this.georef.originLat) * METERS_PER_DEG_LAT;
        // meters → model units; north maps to -Z by default, +Z when zNorthPositive
        const northSign = this.georef.zNorthPositive ? 1 : -1;
        const mx = eastM / this.georef.metersPerUnit;
        const mz = (northSign * northM) / this.georef.metersPerUnit;
        // rotate around +Y by rotationDeg
        const rx = mx * this.cosR - mz * this.sinR;
        const rz = mx * this.sinR + mz * this.cosR;
        return { x: rx + this.georef.originModel.x, z: rz + this.georef.originModel.z };
    }

    /** model-space x/z → lat/lng (inverse of geoToModel). */
    public modelToGeo(x: number, z: number): { lat: number; lng: number } {
        const dx = x - this.georef.originModel.x;
        const dz = z - this.georef.originModel.z;
        // inverse rotation
        const mx = dx * this.cosR + dz * this.sinR;
        const mz = -dx * this.sinR + dz * this.cosR;
        const eastM = mx * this.georef.metersPerUnit;
        const northSign = this.georef.zNorthPositive ? 1 : -1;
        const northM = northSign * mz * this.georef.metersPerUnit;
        return {
            lat: this.georef.originLat + northM / METERS_PER_DEG_LAT,
            lng: this.georef.originLng + eastM / this.mPerDegLng,
        };
    }
}
